import React, {
    useRef,
    useCallback,
    useMemo,
    useEffect,
    useState,
    Fragment,
} from "react";
import { useSearchParams } from "react-router-dom";

import api from "services/api";
import sanitizeObject from "common/sanitize-object";
import serveRequestErrors from "common/serve-request-errors";
import AppTable, { AppTableCell, AppTableMoreIcon } from "components/app-table";
import AppEmptyState from "components/app-empty-state";
import { formatDate } from "common/calendar";
import { formatTimeValue } from "common/time";
import AppStatus from "components/app-status";
import ConfirmAppointmentModal from "components/pages/appointment/app-confirm-modal";
import MenuItem from "@mui/material/MenuItem";
import Menu from "@mui/material/Menu";

function PageAppointment() {
    const [searchParams, setSearchParams] = useSearchParams();
    const memoSetSearchParams = useRef(setSearchParams);
    const ConfirmAppointmentRef = useRef();
    const selectedRowRef = useRef(null);
    const [anchorEl, setAnchorEl] = useState(null);

    const paramsRef = useRef({
        page: parseInt(searchParams.get("page")) || 1,
        sort: searchParams.get("sort") || "",
        statusListStr: ["UPCOMING", "ACKNOWLEDGED"],
    });

    const [data, setData] = useState({
        page: paramsRef.current.page,
        size: 10,
        total: 0,
        offset: 0,
        totalElements: 0,
        prev: false,
        next: false,
        items: [],
    });


    const columns = useMemo(() => [
        {
            Header: "Mentor Name",
            accessor: "mentorName",
            disableSortBy: false,
            Cell: ({ row }) => <AppTableCell left value={row.original.mentorName} />,
        },
        {
            Header: "Appointment Date",
            accessor: "appointmentDate",
            disableSortBy: false,
            Cell: ({ row }) => <AppTableCell left value={formatDate(row.original.appointmentDate)} />,
        },
        {
            Header: "Start Time",
            accessor: "appointmentStartTime",
            disableSortBy: false,
            Cell: ({ row }) => <AppTableCell left value={formatTimeValue(row.original.appointmentStartTime)} />,
        },
        {
            Header: "End Time",
            accessor: "appointmentEndTime",
            disableSortBy: false,
            Cell: ({ row }) => <AppTableCell left value={formatTimeValue(row.original.appointmentEndTime)} />,
        },
        {
            Header: "Remark",
            accessor: "remark",
            disableSortBy: false,
            Cell: ({ row }) =>
                <div className="remark-column">
                    <AppTableCell left value={row.original.remark} />
                </div>
            ,
        },
        {
            Header: "Status",
            accessor: "status",
            disableSortBy: false,
            Cell: ({ row }) => <AppStatus status={row.original.status} />,
        },
        {
            Header: "",
            accessor: "*",
            disableSortBy: true,
            Cell: ({ row }) => (
                <AppTableMoreIcon onClick={(event) => onHandleTableMenu(event, row.original.id)} />
            ),
        },
    ], []);

    const [loading, setLoading] = useState(true);
    const isEmptyState = useMemo(() => !loading && data.items.length === 0, [loading, data.items]);

    const onHandleGetList = useCallback(async () => {
        setLoading(true);

        setData({
            page: paramsRef.current.page,
            size: 10,
            total: 0,
            offset: 0,
            totalElements: 0,
            prev: false,
            next: false,
            items: [],
        });

        try {
            const payload = { ...paramsRef.current, size: 10 };
            memoSetSearchParams.current(sanitizeObject(payload), { replace: true });
            payload.page = paramsRef.current.page - 1;
            const response = await api.get.appointment.list(sanitizeObject(payload));

            setData({
                page: paramsRef.current.page,
                size: 10,
                total: response ? response.totalPages : 1,
                offset: response?.pageable?.offset || 0,
                totalElements: response ? response.totalElements : response?.content?.length || 0,
                prev: response ? !response.first : false,
                next: response ? !response.last : false,
                items: response?.content ?? [],
                status: response?.pageable,
            });
        } catch (error) {
            const wasCancelled = serveRequestErrors(error);
            if (wasCancelled) return;
        }

        setLoading(false);
    }, []);


    const onHandleTableMenu = (event, row) => {
        setAnchorEl(event.currentTarget);
        selectedRowRef.current = row;
    };


    const onhandleCloseMenu = useCallback(() => {
        const el = document.querySelector(".table__sort");
        el?.classList.toggle("table__sort--active");
        setAnchorEl(null);
        // onHandleGetList();
    }, []);

    const onHandlePagination = useCallback((event) => {
        const ctrl = event.currentTarget?.getAttribute("data-ctrl");
        if (ctrl === "prev" && paramsRef.current.page > 1) {
            paramsRef.current.page -= 1;
        } else if (ctrl === "next" && paramsRef.current.page < data.total) {
            paramsRef.current.page += 1;
        } else if (!ctrl) {
            paramsRef.current.page = event.target.value;
        }
        onHandleGetList();
    }, [onHandleGetList, data.total]);

    const onHandleSort = useCallback((id, order) => {
        paramsRef.current.sort = order ? `${id},${order}` : "";
        paramsRef.current.page = 1;
        onHandleGetList();
    }, [onHandleGetList]);

    useEffect(() => {
        onHandleGetList();
    }, [onHandleGetList]);

    return (
        <div className="page-appointment-history">
            <div className="company-appointment-history">
                <div className="app-appointment-history">
                    {!isEmptyState && (
                        <Fragment>
                            <AppTable
                                pages={data}
                                onHandlePagination={onHandlePagination}
                                onHandleSort={onHandleSort}
                                columns={columns}
                            />

                            <Menu
                                classes={{ root: "app-table-menu" }}
                                anchorEl={anchorEl}
                                open={!!anchorEl}
                                onClose={onhandleCloseMenu}
                                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                                transformOrigin={{ vertical: "top", horizontal: "right" }}
                            >
                                <MenuItem
                                    onClick={() => {
                                        ConfirmAppointmentRef.current.onHandleShow(selectedRowRef.current, "ACKNOWLEDGED")
                                        setAnchorEl(null);
                                    }}
                                >
                                    Mark as Acknowledged
                                </MenuItem>
                            </Menu>
                        </Fragment>
                    )}
                    {isEmptyState && (
                        <AppEmptyState
                            title="No records found"
                            description="You don’t have any records yet"
                            disabledButton
                        />

                    )}

                    <ConfirmAppointmentModal
                        ref={ConfirmAppointmentRef}
                        onHandleGetList={onHandleGetList}
                    />
                </div>
            </div>
        </div>
    );
}

export default PageAppointment;
